import { siteApi } from "@api";
import { closeDeposit } from "@components/utils/DepositDrawer/utils";
import { closeIframeDialog } from "@components/utils/IframeDialog";
import { useStatsigTracking } from "@configs/statsig";
import { WSDepositSuccessEvent } from "@constants/ws";
import { AppDispatch } from "@stores";
import preferenceSlice from "@stores/preference/slices";
import { fAmount } from "kz-ui-sdk";
import moment from "moment";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useWSDepositSuccess } from "./useAppWS";
import usePreference from "./usePreference";

export default function useDepositNotification() {
  const { t } = useTranslation();
  const [markAsReceived] = siteApi.useMarkAsReceivedMutation();
  const { pendingDepositOrderRef } = usePreference();
  const dispatch = useDispatch<AppDispatch>();
  const { trackDepositSuccess } = useStatsigTracking();

  const handleDepositSuccess = useCallback(
    (event: WSDepositSuccessEvent) => {
      const fundingTx = event.detail.fundingTx;

      // mark as received event
      if (fundingTx) {
        markAsReceived({
          orderRef: fundingTx.orderRef,
          timestamp: moment().toISOString(),
        });
      }

      if (pendingDepositOrderRef !== event.detail.fundingTx?.orderRef) {
        // deposit status watcher already handled this event
        return;
      }

      // Track final deposit success (actual money received)
      if (fundingTx) {
        trackDepositSuccess(Number(fundingTx.netAmount) || 0, fundingTx.orderRef, {
          currency: fundingTx.reqCurrency || fundingTx.netCurrency,
          method: fundingTx.method ?? undefined,
          providerKey: fundingTx.providerKey,
        });
      }

      toast.success(t("Deposit amount completed", { amount: fAmount(fundingTx?.netAmount) }), {
        id: `deposit-toast-${fundingTx?.orderRef}`,
      });
      closeIframeDialog({
        id: `deposit-${fundingTx?.orderRef}`,
      });
      // close deposit drawer
      closeDeposit();

      // clear pending deposit order ref to stop polling
      if (pendingDepositOrderRef === fundingTx?.orderRef) {
        dispatch(preferenceSlice.actions.setPendingDepositOrderRef(null));
      }
    },
    [t, markAsReceived, pendingDepositOrderRef, dispatch, trackDepositSuccess],
  );

  useWSDepositSuccess({ cb: handleDepositSuccess });
}
